/**
 * Admin-Page für Visual-Test-Suite-Protokolle (T-Phase, 2026-05-02).
 *
 * Listet die Runs (POST /api/admin/test-protocols) absteigend nach run_at.
 * Click → Detail-View mit Summary-Markdown + per-Card-Mismatches aus
 * details_json. Ziel: Verbesserung B-Run vs F-Run messbar machen.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const INITIAL_BASE = document.baseURI;

interface ProtocolListItem {
  id: string;
  run_at: string;
  kind: "highlight" | "ground_truth" | "combined";
  validator_enabled: boolean;
  backend: string;
  cards_total: number;
  cards_pass: number;
  cells_total: number;
  cells_pass: number;
  highlight_pass_rate: number | null;
  gt_pass_rate: number | null;
  klasse_5b_rate: number | null;
  notes: string | null;
}

interface ProtocolDetail extends ProtocolListItem {
  details_json: unknown;
  summary_md: string;
  created_by: string | null;
}

function pct(n: number | null): string {
  if (n == null) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function fmtRunAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

/** Tiny markdown renderer — paragraphs, headings, lists, code-blocks, bold/italic.
 *  Sufficient for our generated summary_md. Avoids a heavy dependency. */
function renderMarkdown(md: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split("\n");
  const out: string[] = [];
  let inCode = false;
  let inList = false;
  let inOl = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    if (line.trim().startsWith("```")) {
      if (!inCode) { out.push("<pre><code>"); inCode = true; } else { out.push("</code></pre>"); inCode = false; }
      continue;
    }
    if (inCode) { out.push(escape(line)); continue; }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      if (inList) { out.push(inOl ? "</ol>" : "</ul>"); inList = false; }
      out.push(`<h${h[1]!.length}>${escape(h[2]!)}</h${h[1]!.length}>`);
      continue;
    }
    const li = /^(\s*)[*-]\s+(.*)$/.exec(line);
    const oli = /^(\s*)\d+\.\s+(.*)$/.exec(line);
    if (li || oli) {
      if (!inList) { out.push(oli ? "<ol>" : "<ul>"); inList = true; inOl = !!oli; }
      const text = (li ? li[2] : oli![2]) ?? "";
      out.push(`<li>${inlineFmt(text)}</li>`);
      continue;
    }
    if (inList) { out.push(inOl ? "</ol>" : "</ul>"); inList = false; }
    if (line.trim() === "") { out.push(""); continue; }
    out.push(`<p>${inlineFmt(line)}</p>`);
  }
  if (inList) out.push(inOl ? "</ol>" : "</ul>");
  if (inCode) out.push("</code></pre>");
  return out.join("\n");

  function inlineFmt(s: string): string {
    let t = escape(s);
    t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    t = t.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
    t = t.replace(/`([^`]+)`/g, "<code>$1</code>");
    return t;
  }
}

export function AdminTestProtocolsPage() {
  const { id: urlId } = useParams<{ id?: string }>();
  const [list, setList] = useState<ProtocolListItem[] | null>(null);
  const [detail, setDetail] = useState<ProtocolDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDetailsJson, setShowDetailsJson] = useState(false);
  const [running, setRunning] = useState<"highlight" | "ground_truth" | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const refreshList = () => {
    return fetch(new URL("api/admin/test-protocols", INITIAL_BASE).toString(), {
      credentials: "same-origin",
    })
      .then((r) => {
        if (r.status === 403) throw new Error("admin-only");
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((d: { protocols: ProtocolListItem[] }) => setList(d.protocols))
      .catch((e: Error) => setError(e.message === "admin-only" ? "Nur für Admins." : "Fehler beim Laden."));
  };

  useEffect(() => { refreshList(); }, []);

  // Deep-Link via /admin/test-protocols/:id — Detail beim Mount öffnen, damit
  // Links aus der Werkstatt (HighlightAutoTest „Voll-Detail (JSON) →") direkt
  // funktionieren statt auf /inbox zu fallen.
  useEffect(() => {
    if (urlId && /^[0-9a-f-]{36}$/i.test(urlId)) openDetail(urlId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId]);

  const triggerRun = async (kind: "highlight" | "ground_truth") => {
    setRunning(kind);
    setRunError(null);
    try {
      const path = kind === "highlight" ? "run-highlight" : "run-ground-truth";
      const res = await fetch(new URL(`api/admin/test-protocols/${path}`, INITIAL_BASE).toString(), {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => `${res.status}`);
        throw new Error(`${res.status}: ${txt.slice(0, 200)}`);
      }
      const data = await res.json() as { id: string };
      await refreshList();
      // open detail of the new run automatically
      openDetail(data.id);
    } catch (e: any) {
      setRunError(`${kind}: ${e?.message ?? e}`);
    } finally {
      setRunning(null);
    }
  };

  const openDetail = (id: string) => {
    setShowDetailsJson(false);
    fetch(new URL(`api/admin/test-protocols/${id}`, INITIAL_BASE).toString(), {
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: ProtocolDetail | null) => setDetail(d));
  };

  const previousRun = useMemo<ProtocolListItem | null>(() => {
    if (!detail || !list) return null;
    const idx = list.findIndex((p) => p.id === detail.id);
    if (idx < 0 || idx >= list.length - 1) return null;
    return list[idx + 1] ?? null;
  }, [detail, list]);

  const renderDelta = (curr: number | null, prev: number | null): string => {
    if (curr == null || prev == null) return "";
    const d = curr - prev;
    const sign = d > 0 ? "+" : "";
    return ` (${sign}${(d * 100).toFixed(1)}pp)`;
  };

  if (error) return (
    <div className="doc-page">
      <h1>Test-Protokoll</h1>
      <div className="empty-state"><h3>Keine Daten</h3><p className="muted">{error}</p></div>
    </div>
  );
  if (!list) return <div className="doc-page"><h1>Lade…</h1></div>;

  return (
    <div className="doc-page">
      <header className="doc-header">
        <h1>Test-Protokoll</h1>
        <p className="doc-subtitle">
          Deterministische Tests auf alle Karten. Klick startet — Ergebnis erscheint unten.
        </p>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }}>
          <button
            type="button"
            className="primary"
            disabled={running !== null}
            onClick={() => triggerRun("highlight")}
          >
            {running === "highlight" ? "Highlight läuft…" : "Highlight-Test starten"}
          </button>
          <button
            type="button"
            className="primary"
            disabled={running !== null}
            onClick={() => triggerRun("ground_truth")}
          >
            {running === "ground_truth" ? "GT-Vergleich läuft…" : "GT-Vergleich starten"}
          </button>
          {runError && <span className="error">{runError}</span>}
        </div>
      </header>

      {list.length === 0 && (
        <div className="empty-state"><h3>Noch keine Runs</h3>
          <p className="muted">Test-Suite zum ersten Mal über CLI laufen lassen.</p>
        </div>
      )}

      {list.length > 0 && (
        <table className="ts-table" style={{ marginBottom: 24 }}>
          <thead>
            <tr>
              <th>Datum</th><th>Art</th><th>Validator</th><th>Backend</th>
              <th>Cards</th><th>Highlight %</th><th>GT %</th><th>5b-Rate</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} onClick={() => openDetail(p.id)} style={{ cursor: "pointer" }}>
                <td>{fmtRunAt(p.run_at)}</td>
                <td>{p.kind}</td>
                <td>{p.validator_enabled ? "✅" : "❌"}</td>
                <td><code>{p.backend}</code></td>
                <td>{p.cards_pass}/{p.cards_total}</td>
                <td>{pct(p.highlight_pass_rate)}</td>
                <td>{pct(p.gt_pass_rate)}</td>
                <td>{pct(p.klasse_5b_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && (
        <div className="audit-modal-backdrop" onClick={() => setDetail(null)}>
          <div className="audit-modal" style={{ maxWidth: 800, width: "92%" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>
              Test-Run {fmtRunAt(detail.run_at)} — {detail.kind}
              {previousRun && (
                <small style={{ marginLeft: 12, color: "var(--text-muted)", fontWeight: 400 }}>
                  Δ vs voriger Run
                </small>
              )}
            </h3>

            <dl className="kv-grid" style={{ marginBottom: 12 }}>
              <dt>Backend</dt>
              <dd><code>{detail.backend}</code></dd>
              <dt>Validator aktiv</dt>
              <dd>{detail.validator_enabled ? "✅" : "❌"}</dd>
              <dt>Cards (pass/total)</dt>
              <dd>{detail.cards_pass}/{detail.cards_total}</dd>
              <dt>Cells (pass/total)</dt>
              <dd>{detail.cells_pass}/{detail.cells_total}</dd>
              <dt>Highlight pass-rate</dt>
              <dd>
                {pct(detail.highlight_pass_rate)}
                {previousRun && renderDelta(detail.highlight_pass_rate, previousRun.highlight_pass_rate)}
              </dd>
              <dt>GT pass-rate</dt>
              <dd>
                {pct(detail.gt_pass_rate)}
                {previousRun && renderDelta(detail.gt_pass_rate, previousRun.gt_pass_rate)}
              </dd>
              <dt>Klasse-5b-Rate</dt>
              <dd>
                {pct(detail.klasse_5b_rate)}
                {previousRun && renderDelta(detail.klasse_5b_rate, previousRun.klasse_5b_rate)}
              </dd>
              {detail.notes && <><dt>Notizen</dt><dd>{detail.notes}</dd></>}
            </dl>

            <div
              className="markdown"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(detail.summary_md) }}
            />

            <button
              type="button"
              className="ct-audit-btn"
              onClick={() => setShowDetailsJson((v) => !v)}
            >
              {showDetailsJson ? "Details ausblenden" : "Per-Card Details (JSON)"}
            </button>
            {showDetailsJson && (
              <pre style={{ maxHeight: 400, overflow: "auto" }}>
                {JSON.stringify(detail.details_json, null, 2)}
              </pre>
            )}

            <button type="button" className="modal-close" onClick={() => setDetail(null)}>
              Schließen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
